import moment from "moment"
import { SUPPORTER_RECIPIENT_KEY, USER_RECIPIENT_KEY } from "shared/imports/sharedConstants"
import { extractAssignmentCellNames } from "app/static/frontend/sheets/mantine-sheet/mantine-sheet-helper-functions"

const amountRange = (row, id, filterValue) => {
    const minValue = !isNaN(filterValue[0]) && filterValue[0] !== "" ? Number(filterValue[0]) : Number.NEGATIVE_INFINITY
    const maxValue = !isNaN(filterValue[1]) && filterValue[1] !== "" ? Number(filterValue[1]) : Number.POSITIVE_INFINITY
    const cellValue = row.getValue(id)
    const filterCleared = filterValue.every((value) => value === "" || isNaN(value))

    if (filterCleared) {
        return true
    }

    return cellValue !== null && minValue <= cellValue && cellValue <= maxValue
}

const currencyAmountRange = (row, id, filterValue) => {
    const minValue = filterValue[0] ? Number(filterValue[0]) : Number.NEGATIVE_INFINITY
    const maxValue = filterValue[1] ? Number(filterValue[1]) : Number.POSITIVE_INFINITY
    const cellValue = Number(row.getValue(id) / 100)
    const filterCleared = filterValue.every((value) => !value)

    if (filterCleared) {
        return true
    }

    return minValue <= cellValue && cellValue <= maxValue
}

const multiSelect = (row, id, filterValue) => {
    if (!filterValue.length) {
        return true
    }

    const rowValue = row.getValue(id)
    // rowValue here can be either a single value or many in select-many cases
    const foundInCell = filterValue.find((val) => {
        if (Array.isArray(rowValue)) {
            return rowValue.includes(val)
        }
        return val === rowValue
    })

    // 0 is falsey, so if it returns 0 and not null that means we found an object, so return true!
    if (foundInCell || foundInCell === 0) {
        return true
    }

    return false
}

const dateRange = (row, id, filterValue) => {
    const beforeDate = filterValue[1]
    const afterDate = filterValue[0]
    const rowValue = row.getValue(id)
    const cellDate = moment(rowValue)

    const filterCleared = filterValue.every((value) => !value)

    if (filterCleared) {
        return true
    }

    // moment(undefined) returns the current time, which is not great.
    if (rowValue === null || rowValue === undefined) {
        return false
    }
    if (beforeDate && !afterDate) {
        // before date is selected only
        return cellDate.isBefore(beforeDate)
    } else if (afterDate && !beforeDate) {
        // after date is selected only
        return cellDate.isAfter(afterDate)
    }

    // both the before date and the after dates are selected
    return cellDate.isBefore(beforeDate) && cellDate.isAfter(afterDate)
}

const dateRangeNoYear = (row, id, filterValue) => {
    const filterCleared = filterValue.every((value) => !value)
    if (filterCleared) {
        return true
    }

    // apply simple heuristic to get the correct day of year order regardless
    // of leap year
    const getDayOfYearValue = (date) => date.month() * 100 + date.date()

    const minDate = filterValue[0]
    const maxDate = filterValue[1]
    const rowValue = row.getValue(id)
    const cellDate = moment(rowValue)

    // moment(undefined) returns the current time, which is not great.
    if (rowValue === null || rowValue === undefined) {
        return false
    }
    const rowDayOfYearValue = getDayOfYearValue(cellDate)
    if (minDate && !maxDate) {
        return rowDayOfYearValue > getDayOfYearValue(moment(minDate))
    } else if (maxDate && !minDate) {
        return rowDayOfYearValue < getDayOfYearValue(moment(maxDate))
    }
    return (
        rowDayOfYearValue > getDayOfYearValue(moment(minDate)) && rowDayOfYearValue < getDayOfYearValue(moment(maxDate))
    )
}

const checkbox = (row, id, filterValue) => {
    let value = row.getValue(id)
    if (typeof value === "object" && value !== null && "value" in value) {
        value = value.value
    }
    return filterValue === String(value)
}

const contains = (row, id, filterValue) =>
    Boolean(
        row?.getValue(id)?.toString()?.toLowerCase()?.trim()?.includes?.(filterValue.toString().toLowerCase().trim()),
    )

const containsOrganizationName = (row, id, filterValue) => {
    const orgNames = row.getValue(id).cell_value ?? ""
    return Boolean(orgNames?.toLowerCase()?.trim()?.includes?.(filterValue.toString().toLowerCase().trim()))
}
// extracted from AssignmentCell.tsx
const containsUserAssignment = (row, id, filterValue) => {
    // assignment data is a mapping of user objects, not a direct string
    const assignments = row.getValue(id) ?? []
    const assignmentSearchString = extractAssignmentCellNames(
        assignments,
        USER_RECIPIENT_KEY,
        "first_name",
        "last_name",
    )

    return Boolean(
        assignmentSearchString?.toLowerCase()?.trim()?.includes?.(filterValue.toString().toLowerCase().trim()),
    )
}

// extracted from AssignmentCell.tsx
const containsSupporterAssignment = (row, id, filterValue) => {
    // assignment data is a mapping of user objects, not a direct string
    const assignments = row.getValue(id) ?? []
    const assignmentSearchString = extractAssignmentCellNames(
        assignments,
        SUPPORTER_RECIPIENT_KEY,
        "firstname",
        "lastname",
    )

    return Boolean(
        assignmentSearchString?.toLowerCase()?.trim()?.includes?.(filterValue.toString().toLowerCase().trim()),
    )
}

const containsInIssue = (row, id, filterValue) => {
    // assignment data is a mapping of user objects, not a direct string
    const issues = row.getValue(id) ?? []
    const issuesSearchString = issues
        ?.map((issue) => issue.name)
        ?.filter((i) => i)
        ?.join(", ")

    return Boolean(issuesSearchString?.toLowerCase()?.trim()?.includes?.(filterValue.toString().toLowerCase().trim()))
}

/**
 * A mapping of <String, Function> for Mantine-React-Table column definition
 * filter functions.
 */
export const filterFns = {
    amountRange,
    currencyAmountRange,
    multiSelect,
    dateRange,
    dateRangeNoYear,
    checkbox,
    contains,
    containsOrganizationName,
    containsUserAssignment,
    containsSupporterAssignment,
    containsInIssue,
}
